import React, { FC } from 'react'
import { Checkbox, Input, Text } from 'components/common'
import { SAGA_ARENA_USAGE_TYPE } from 'config'
import { Tooltip } from 'antd'
import { InfoCircleTwoTone } from '@ant-design/icons'
import {
  AncillaryFacility,
  AncillaryFacilityOptions,
  SagaArenaBookingTargetType,
} from './SagaArenaProductSelectCard'
import { ProductAvailabilitiesQuery } from 'lib/graphql/storeadmin'

type UpdateUsageTypeQuantity = (date: string, newQuantity: number) => void

type SagaArenaUsageTypeSelectProps = {
  availableData: {
    facilities: ProductAvailabilitiesQuery
    options: AncillaryFacilityOptions[]
  }
  date: string
  eventOfUsageValue: boolean
  extensionValue: number
  isPurposeSelected: boolean
  noPerformanceValue: boolean
  priceLoading: boolean
  setupTeardownValue: number
  updateExtensionQuantity: UpdateUsageTypeQuantity
  updateSetupTeardownQuantity: UpdateUsageTypeQuantity
  updateNoPerformanceQuantity: UpdateUsageTypeQuantity
  updateEventOfUsageQuantity: UpdateUsageTypeQuantity
  handleChangeAncillaryFacility: (
    date: string,
    checkedVariantIds: string[]
  ) => void
  type: SagaArenaBookingTargetType
  selectedAncillaryFacilities: AncillaryFacility[]
  canOrder: boolean
}

const SagaArenaUsageTypeSelectCard: FC<SagaArenaUsageTypeSelectProps> =
  React.memo((props) => {
    const {
      availableData,
      date,
      eventOfUsageValue,
      extensionValue,
      isPurposeSelected,
      noPerformanceValue,
      priceLoading,
      updateExtensionQuantity,
      updateNoPerformanceQuantity,
      updateSetupTeardownQuantity,
      updateEventOfUsageQuantity,
      handleChangeAncillaryFacility,
      setupTeardownValue,
      selectedAncillaryFacilities,
      type,
      canOrder,
    } = props

    // dateと一致するバリエーションIDを配列に格納する
    const selectedFacilityIdsForDate = selectedAncillaryFacilities
      .filter((facility) =>
        facility.arenaBookingInfo.some((info) => info.dateOfUse === date)
      )
      .map((facility) => facility.variantId)

    return (
      <li className="rounded-sm border border-slate-200 bg-slate-50 shadow-sm p-4">
        <div className="font-bold mb-2">{date.split('-').join(' - ')}</div>
        <div className="grid grid-cols-12 gap-x-8 pl-4">
          <div className="col-span-5 grid grid-cols-2 gap-x-8">
            <div>
              <div className="space-x-1 mb-px">
                <Text>催事</Text>
                <Tooltip
                  title={`${date}に催事が発生する場合はチェックを入れてください`}
                >
                  <InfoCircleTwoTone />
                </Tooltip>
              </div>
              <Checkbox
                disabled={!isPurposeSelected || priceLoading || !canOrder}
                onChange={(e) =>
                  updateEventOfUsageQuantity(date, e.target.checked ? 1 : 0)
                }
                checked={eventOfUsageValue}
              >
                {SAGA_ARENA_USAGE_TYPE.EVENT + '有'}
              </Checkbox>
            </div>
            <div>
              <div className="space-x-1 mb-px">
                <Text>休演</Text>
                <Tooltip
                  title={`${date}に休演が発生する場合はチェックを入れてください`}
                >
                  <InfoCircleTwoTone />
                </Tooltip>
              </div>
              <Checkbox
                disabled={!isPurposeSelected || priceLoading || !canOrder}
                onChange={(e) =>
                  updateNoPerformanceQuantity(date, e.target.checked ? 1 : 0)
                }
                checked={noPerformanceValue}
              >
                {SAGA_ARENA_USAGE_TYPE.NO_PERFORMANCE + '有'}
              </Checkbox>
            </div>
          </div>
          <div className="col-span-7 grid grid-cols-2 gap-x-8">
            {type === 'sub' && (
              <div>
                <div className="space-x-1 mb-px">
                  <Text>控室</Text>
                  <Tooltip
                    title={
                      <p className="mb-0">
                        {date}
                        に催事と同時利用する施設にチェックを入れてください。
                        <br />
                        予約不可の施設はチェックできません。
                      </p>
                    }
                  >
                    <InfoCircleTwoTone />
                  </Tooltip>
                </div>

                <Checkbox.Group
                  onChange={(checkedValues) =>
                    handleChangeAncillaryFacility(
                      date,
                      checkedValues.map(String)
                    )
                  }
                  disabled={!isPurposeSelected || priceLoading || !canOrder}
                  options={availableData.options}
                  value={selectedFacilityIdsForDate}
                />
              </div>
            )}
            <div>
              <div className="space-x-1">
                <Text>設営・撤去</Text>
                <Tooltip title={`${date} の設営撤去の合計を入力してください`}>
                  <InfoCircleTwoTone />
                </Tooltip>
              </div>
              <Input
                suffix="時間"
                type="number"
                min={0}
                max={999}
                onChange={(e) =>
                  updateSetupTeardownQuantity(date, Number(e.target.value))
                }
                value={setupTeardownValue}
                disabled={!isPurposeSelected || priceLoading || !canOrder}
              />
            </div>
            {type === 'main' && (
              <div>
                <div className="space-x-1">
                  <Text>延長</Text>
                  <Tooltip title={`${date} の延長時間の合計を入力してください`}>
                    <InfoCircleTwoTone />
                  </Tooltip>
                </div>
                <Input
                  suffix="時間"
                  type="number"
                  min={0}
                  max={999}
                  onChange={(e) =>
                    updateExtensionQuantity(date, Number(e.target.value))
                  }
                  value={extensionValue}
                  disabled={!isPurposeSelected || priceLoading || !canOrder}
                />
              </div>
            )}
          </div>
        </div>
      </li>
    )
  })

export default SagaArenaUsageTypeSelectCard
